import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import PasswordChecklist from "react-password-checklist";
import AccountRecoveryService from "../../services/account-recovery/accountRecovery";
import logo_user from "../../assests/images/iCareNet logo_page-0001.jpg";
import "../Auth/securityReset.css";

const RTL_LANGS = ["ar"];

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

const AccountRecoveryReset = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("userLanguage", JSON.stringify({ code: newLang }));
    const dir = RTL_LANGS.includes(newLang) ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = newLang;
  }, [i18n]);

  useEffect(() => {
    const dir = RTL_LANGS.includes(i18n.language) ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const [state, setState] = useState({
    loading: true,
    valid: false,
    username: "",
    passwordPolicy: null,
    accountPending: false,
    walletPending: false,
  });

  const [accountPassword, setAccountPassword] = useState("");
  const [accountConfirm, setAccountConfirm] = useState("");
  const [accountError, setAccountError] = useState("");
  const [accountShow, setAccountShow] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);

  const [walletPassword, setWalletPassword] = useState("");
  const [walletConfirm, setWalletConfirm] = useState("");
  const [walletError, setWalletError] = useState("");
  const [walletShow, setWalletShow] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await AccountRecoveryService.verifyResetToken({ token });
        if (!active) return;
        if (res?.status) {
          const d = res.data || {};
          setState({
            loading: false,
            valid: true,
            username: d.username || "",
            passwordPolicy: d.passwordPolicy || null,
            accountPending: !!d.accountPending,
            walletPending: !!d.walletPending,
          });
        } else {
          setState((s) => ({ ...s, loading: false, valid: false }));
          toast.error(res?.data?.description || res?.message || t("invalid_or_expired_link", "Invalid or expired link"));
          setTimeout(() => navigate("/login"), 3000);
        }
      } catch (e) {
        if (!active) return;
        setState((s) => ({ ...s, loading: false, valid: false }));
        toast.error(t("invalid_or_expired_link", "Invalid or expired link"));
        setTimeout(() => navigate("/login"), 3000);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checklistRules = (() => {
    const rules = ["match"];
    if (state.passwordPolicy) {
      if (state.passwordPolicy.minLength) rules.push("minLength");
      if (state.passwordPolicy.mixedCase) rules.push("capital", "lowercase");
      if (state.passwordPolicy.numbers) rules.push("number");
      if (state.passwordPolicy.specialChars) rules.push("specialChar");
    }
    return rules;
  })();

  const submitAccount = async () => {
    setAccountError("");
    if (accountPassword !== accountConfirm) {
      setAccountError(t("passwords_do_not_match", "Passwords do not match"));
      return;
    }
    if (!accountPassword) {
      setAccountError(t("this_field_is_required", "This field is required"));
      return;
    }
    setAccountLoading(true);
    try {
      const res = await AccountRecoveryService.setAccountPassword({
        token,
        password: accountPassword,
        passwordConfirm: accountConfirm,
      });
      if (res?.status) {
        toast.success(t("password_updated"));
        setState((s) => ({
          ...s,
          accountPending: false,
          walletPending: !!res.data?.walletPending,
        }));
        setAccountPassword("");
        setAccountConfirm("");
      } else {
        setAccountError(res?.data?.description || res?.message || t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
      }
    } catch (e) {
      setAccountError(t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
    } finally {
      setAccountLoading(false);
    }
  };

  const submitWallet = async () => {
    setWalletError("");
    if (walletPassword !== walletConfirm) {
      setWalletError(t("passwords_do_not_match", "Passwords do not match"));
      return;
    }
    if (!walletPassword) {
      setWalletError(t("this_field_is_required", "This field is required"));
      return;
    }
    setWalletLoading(true);
    try {
      const res = await AccountRecoveryService.setWalletPassword({
        token,
        password: walletPassword,
        passwordConfirm: walletConfirm,
      });
      if (res?.status) {
        toast.success(t("password_updated"));
        setState((s) => ({
          ...s,
          walletPending: false,
          accountPending: !!res.data?.accountPending,
        }));
        setWalletPassword("");
        setWalletConfirm("");
      } else {
        setWalletError(res?.data?.description || res?.message || t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
      }
    } catch (e) {
      setWalletError(t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
    } finally {
      setWalletLoading(false);
    }
  };

  if (state.loading) {
    return (
      <section className="loginSection sr-section">
        <div className="container centerDiv">
          <div className="sr-card sr-card--center">
            <div className="sr-spinner" aria-hidden="true"></div>
            <p className="sr-muted">{t("please_wait", "Please wait...")}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!state.valid) return null;

  const allDone = !state.accountPending && !state.walletPending;

  const StatusChip = ({ label, pending }) => (
    <span className={`sr-chip ${pending ? "sr-chip--pending" : "sr-chip--done"}`}>
      <span className="sr-chip__dot" aria-hidden="true"></span>
      {label}: {pending ? t("sr_pending", "Pending") : t("sr_done", "Done")}
    </span>
  );

  return (
    <section className="loginSection sr-section">
      <div className="container centerDiv">
        <div className="sr-card" style={{ position: "relative" }}>
          <button
            type="button"
            onClick={toggleLanguage}
            className="lang-switcher-btn lang-switcher-btn--card"
          >
            {i18n.language === "ar" ? "EN" : "عربي"}
          </button>
          <div className="sr-header">
            <div className="sr-logo">
              <img src={logo_user} alt="Logo" />
            </div>
            <h4 className="sr-title">{t("set_new_password")}</h4>
            <p className="sr-muted">
              {t(
                "account_recovery_reset_intro",
                "Your account recovery request has been approved. Please set your new passwords below."
              )}
            </p>
            {state.username && (
              <div className="sr-user">
                <span className="sr-user__label">{t("username", "Username")}</span>
                <span className="sr-user__value">{state.username}</span>
              </div>
            )}
            <div className="sr-steps">
              <StatusChip label={t("account_password")} pending={state.accountPending} />
              <StatusChip label={t("wallet_password")} pending={state.walletPending} />
            </div>
          </div>

          {allDone && (
            <div className="sr-done">
              <div className="sr-done__icon" aria-hidden="true">
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <p>{t("security_reset_all_done", "All passwords have been set. You can now sign in.")}</p>
              <button type="button" className="sr-btn" onClick={() => navigate("/login")}>
                {t("Login", "Login")}
              </button>
            </div>
          )}

          {state.accountPending && (
            <div className="sr-panel">
              <div className="sr-panel__head">
                <span className="sr-panel__index">1</span>
                <h5>{t("account_password")}</h5>
              </div>
              <div className="sr-field">
                <label htmlFor="accountPassword">{t("newPassword", "New password")}</label>
                <div className="sr-input">
                  <input
                    type={accountShow ? "text" : "password"}
                    id="accountPassword"
                    placeholder={t("newPassword", "New password")}
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="sr-eye"
                    onClick={() => setAccountShow((v) => !v)}
                    aria-label={accountShow ? t("hide", "Hide") : t("show", "Show")}
                  >
                    <EyeIcon open={accountShow} />
                  </button>
                </div>
              </div>
              <div className="sr-field">
                <label htmlFor="accountConfirm">{t("confirm_password", "Confirm password")}</label>
                <div className="sr-input">
                  <input
                    type={accountShow ? "text" : "password"}
                    id="accountConfirm"
                    placeholder={t("confirm_password", "Confirm password")}
                    value={accountConfirm}
                    onChange={(e) => setAccountConfirm(e.target.value)}
                  />
                </div>
                <div className="sr-checklist">
                  <PasswordChecklist
                    rules={checklistRules}
                    minLength={state.passwordPolicy?.minLength}
                    value={accountPassword}
                    valueAgain={accountConfirm}
                  />
                </div>
              </div>
              {accountError && <p className="sr-error">{accountError}</p>}
              <button
                type="button"
                className="sr-btn"
                disabled={accountLoading}
                onClick={submitAccount}
              >
                {accountLoading ? t("please_wait", "Please wait...") : t("set_account_password", "Set account password")}
              </button>
            </div>
          )}

          {state.walletPending && (
            <div className="sr-panel">
              <div className="sr-panel__head">
                <span className="sr-panel__index">{state.accountPending ? 2 : 1}</span>
                <h5>{t("wallet_password")}</h5>
              </div>
              <div className="sr-field">
                <label htmlFor="walletPassword">{t("newPassword", "New password")}</label>
                <div className="sr-input">
                  <input
                    type={walletShow ? "text" : "password"}
                    id="walletPassword"
                    placeholder={t("newPassword", "New password")}
                    value={walletPassword}
                    onChange={(e) => setWalletPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="sr-eye"
                    onClick={() => setWalletShow((v) => !v)}
                    aria-label={walletShow ? t("hide", "Hide") : t("show", "Show")}
                  >
                    <EyeIcon open={walletShow} />
                  </button>
                </div>
              </div>
              <div className="sr-field">
                <label htmlFor="walletConfirm">{t("confirm_password", "Confirm password")}</label>
                <div className="sr-input">
                  <input
                    type={walletShow ? "text" : "password"}
                    id="walletConfirm"
                    placeholder={t("confirm_password", "Confirm password")}
                    value={walletConfirm}
                    onChange={(e) => setWalletConfirm(e.target.value)}
                  />
                </div>
                <div className="sr-checklist">
                  <PasswordChecklist
                    rules={checklistRules}
                    minLength={state.passwordPolicy?.minLength}
                    value={walletPassword}
                    valueAgain={walletConfirm}
                  />
                </div>
              </div>
              {walletError && <p className="sr-error">{walletError}</p>}
              <button
                type="button"
                className="sr-btn"
                disabled={walletLoading}
                onClick={submitWallet}
              >
                {walletLoading ? t("please_wait", "Please wait...") : t("set_wallet_password", "Set wallet password")}
              </button>
            </div>
          )}

          <p className="sr-footnote">{t("account_recovery")}</p>
        </div>
      </div>
    </section>
  );
};

export default AccountRecoveryReset;
